import {Helmet} from 'react-helmet'
import {useTranslation} from '@/translations/i18n.tsx'
import {useURLParsedParams} from '@hooks/useURLParsedParams.ts'
import {useGetUser} from '@/features/users/queries/useGetUser.ts'
import {Spinner} from '@components/ui/spinner/Spinner.tsx'
import {ErrorBox} from '@components/commons/error-box/ErrorBox.tsx'
import {AlertCircleIcon} from '@components/ui/icon/Icon.tsx'
import {AxiosError} from 'axios'
import {DetailsHeader} from '@components/commons/details-header/DetailsHeader.tsx'
import {Outlet} from 'react-router-dom'
import {
    DetailsPageWrapper,
    DetailsPageContent,
    DetailsPageContentTabWrapper
} from '@/components/ui/details-atoms/DetailsAtoms'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {UserActivityLogs} from '@/features/entity-activity-logs/components/user-activity-logs/UserActivityLogs'
import {UserDetailsTabs} from '@/features/users/components/user-details-tab/UserDetailsTab'
import {UserDetailsOptions} from '@/features/users/components/user-details-options/UserDetailsOptions'
import {UserDetailsUrlParams} from '@/types'
import {NoPermissionBox} from '@/components/commons/no-permission-box/NoPermissionBox'
import {usePermissions} from '@/features/auth/store/store'

export const UserDetails = () => {
    const {t} = useTranslation()
    const urlParams = useURLParsedParams(UserDetailsUrlParams)
    const userQuery = useGetUser({
        userId: urlParams.success ? urlParams.data.id : undefined,
        responseType: 'extended',
        options: {enabled: !!urlParams.success}
    })
    const permissions = usePermissions()

    if (!permissions?.users?.read) {
        return <NoPermissionBox />
    }

    if (userQuery.isPending) {
        return (
            <DetailsPageWrapper isSuccess={false}>
                <Spinner size={32} style={{placeSelf: 'center'}} />
            </DetailsPageWrapper>
        )
    }
    if (!urlParams.success || (userQuery.error instanceof AxiosError && userQuery.error.response?.status == 404)) {
        return (
            <DetailsPageWrapper isSuccess={false}>
                <ErrorBox icon={<AlertCircleIcon />} title={t('errors:invalid_link')} />
            </DetailsPageWrapper>
        )
    }
    if (userQuery.error) {
        return (
            <DetailsPageWrapper isSuccess={false}>
                <ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
            </DetailsPageWrapper>
        )
    }

    return (
        <DetailsPageWrapper isSuccess>
            <Helmet title={t('commons:fullname_details_meta_title', {fullName: userQuery.data.fullName})} />
            <DetailsHeader
                title={userQuery.data.fullName}
                subtitle={userQuery?.data?.role?.name}
                startSlot={<Avatar imageUrl={userQuery.data?.avatarUrl ?? undefined} name={userQuery.data.fullName} />}
                endSlot={<UserDetailsOptions user={userQuery.data} />}
            />
            <DetailsPageContent>
                <DetailsPageContentTabWrapper>
                    <UserDetailsTabs user={userQuery.data} />
                    <Outlet />
                </DetailsPageContentTabWrapper>
                <UserActivityLogs />
            </DetailsPageContent>
        </DetailsPageWrapper>
    )
}
